import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PreKPracticeSettings } from '../types';
import { DotPattern } from './types';
import { usePreKTTS } from '../../../hooks/usePreKAudio';
import { NUMERAL_FONT_STYLE } from '../../PreKAssessment/preKConstants';
import {
  PracticeHeader,
  PRACTICE_PHASES,
  PRACTICE_TIMER_CONSTANTS,
  factIdToValue,
  PREK_PRACTICE_TRACKS,
  PRACTICE_VISUAL_CONFIG,
} from '../shared';

interface IDoScreenProps {
  settings: PreKPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
}

const IDoScreen: React.FC<IDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'showing' | 'flashing' | 'blank' | 'waiting'>(
    'showing'
  );
  const [showDots, setShowDots] = useState(false);
  const [currentPattern, setCurrentPattern] = useState<DotPattern | null>(null);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = React.useRef<NodeJS.Timeout[]>([]);

  const { playTTSWithCallback, isTTSPlaying } = usePreKTTS();

  // Generate dot pattern using 3x3 grid
  const generateDotPattern = useCallback((quantity: number): DotPattern => {
    const gridPositions = PRACTICE_VISUAL_CONFIG.SUBITIZING_GRID;

    // Shuffle positions and select the first 'quantity' positions
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Create dots from selected positions
    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`,
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots,
    };
  }, []);

  const currentFactId = factsToPractice[currentFactIndex];
  const currentDigit = currentFactId
    ? factIdToValue(PREK_PRACTICE_TRACKS.SUBITIZING, currentFactId)
    : undefined;

  // Generate new pattern and reset phase when fact changes (combined to avoid race condition)
  useEffect(() => {
    // Guard: Don't proceed if no digit is available
    if (currentDigit === undefined) {
      return;
    }

    // Clear any pending timeouts from previous fact
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    setCurrentPattern(generateDotPattern(currentDigit));
    setShowDots(false);
    setCurrentPhase('showing');
  }, [currentDigit, currentFactIndex, generateDotPattern]);

  // Auto-advance to next fact after showing sequence
  const startAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
    }

    const timer = setTimeout(() => {
      if (currentFactIndex < factsToPractice.length - 1) {
        // Move to next fact - combined effect will handle pattern + phase reset
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // All facts completed, move to next screen
        onComplete();
      }
    }, PRACTICE_TIMER_CONSTANTS.SUBITIZING_AUTO_ADVANCE_MS);

    setAutoAdvanceTimer(timer);
  }, [currentFactIndex, factsToPractice.length, onComplete, autoAdvanceTimer]);

  // Start the subitizing sequence when in showing phase
  useEffect(() => {
    if (currentPhase === 'showing' && currentPattern) {
      // Small delay then start flash
      const timeout1 = setTimeout(() => {
        setCurrentPhase('flashing');
        setShowDots(true);

        // Flash for configured duration
        const timeout2 = setTimeout(() => {
          setShowDots(false);
          setCurrentPhase('blank');

          // Brief blank period, then show number and play TTS
          const timeout3 = setTimeout(() => {
            // Show number card immediately when audio starts
            setCurrentPhase('waiting');

            if (settings.speechEnabled) {
              // Use subitizing-specific I Do audio
              const audioFile = {
                filename: `preKPractice/subitizing/iDo/prek_subitizing_ido_${currentDigit}.mp3`,
                text: `I see ${currentDigit} dot${currentDigit === 1 ? '' : 's'}! Say ${currentDigit}!`,
              };

              // Play "I see [quantity] dots! Say [quantity]!" instruction
              playTTSWithCallback(audioFile, () => {
                // Audio finished, start auto-advance timer
                startAutoAdvance();
              }).catch(() => {
                console.error('❌ IDoScreen subitizing TTS failed for quantity:', currentDigit);
                startAutoAdvance();
              });
            } else {
              startAutoAdvance();
            }
          }, 100);
          timeoutsRef.current.push(timeout3);
        }, PRACTICE_TIMER_CONSTANTS.SUBITIZING_FLASH_MS);
        timeoutsRef.current.push(timeout2);
      }, 500);
      timeoutsRef.current.push(timeout1);
    }
  }, [
    currentPhase,
    currentPattern,
    settings.speechEnabled,
    playTTSWithCallback,
    startAutoAdvance,
    currentDigit,
  ]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [autoAdvanceTimer]);

  // Loading state: show loading screen if no digit is available
  if (currentDigit === undefined || !currentPattern) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={factsToPractice.length > 0 ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1) : 0}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Waiting Phase */}
          {currentPhase === 'showing' && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
                Get ready...
              </div>
              <div className="text-lg text-gray-500">Watch carefully!</div>
            </div>
          )}

          {/* Dots Container - Uses the exact same area as the 4 option cards */}
          {(currentPhase === 'flashing' || currentPhase === 'blank') && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12 relative">
              {/* Create the exact same 4-card layout area */}
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>

              {/* Dots positioned absolutely over this area - 3x3 grid positions */}
              {showDots &&
                currentPattern.dots.map((dot) => (
                  <motion.div
                    key={dot.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="absolute w-12 h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full shadow-lg"
                    style={{
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                ))}
            </div>
          )}

          {/* Waiting Phase - Show large number */}
          {currentPhase === 'waiting' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: 1,
                scale: [1, 1.1, 1],
                boxShadow: [
                  '0 0 0 0 rgba(59, 130, 246, 0.7)',
                  '0 0 0 20px rgba(59, 130, 246, 0)',
                  '0 0 0 0 rgba(59, 130, 246, 0)',
                ],
              }}
              transition={{
                duration: 2,
                repeat: 1,
              }}
              className="w-80 h-80 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-blue-700 mx-auto"
              style={NUMERAL_FONT_STYLE}
            >
              <span className="text-9xl font-bold text-white">{currentDigit}</span>
            </motion.div>
          )}
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IDoScreen;
